import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import API_URL from '../../config/api';
import { showToast } from '../../utils/toast';
import { UK, fmt, fmtMoney, stageMeta, PIPELINE_STAGES } from './pipelineFormShared';

const DEFAULT_COMPLETE = {
  completion_date: new Date().toISOString().split('T')[0],
  final_price: '', stamp_duty: '', legal_fees: '', agent_fees: '', other_costs: '',
  add_to_portfolio: true, archive_property: true, monthly_rent: '',
};

export default function PipelineViewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [item, setItem] = useState<any | null>(null);
  const [linked, setLinked] = useState<any | null>(null);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showComplete, setShowComplete] = useState(false);
  const [completeForm, setCompleteForm] = useState({ ...DEFAULT_COMPLETE });
  const [completing, setCompleting] = useState(false);

  const load = useCallback(async () => {
    if (!token || !id) return;
    setLoading(true);
    const r = await fetch(`${API_URL}/landlord/pipeline/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    const d = await r.json();
    if (!r.ok) { showToast(d.error || 'Not found'); navigate('/landlord/pipeline'); return; }
    setItem(d.item);
    setLinked(d.linked_property || null);
    setTransactions(d.transactions || []);
    const price = d.item.offer_price ?? d.item.asking_price ?? '';
    setCompleteForm(f => ({
      ...f,
      final_price: price != null ? String(price) : '',
      monthly_rent: d.linked_property?.monthly_rent ? String(d.linked_property.monthly_rent) : '',
      add_to_portfolio: d.item.pipeline_type === 'buy',
      archive_property: d.item.pipeline_type === 'sell' && !!d.item.linked_property_id,
    }));
    setLoading(false);
  }, [token, id, navigate]);

  useEffect(() => { load(); }, [load]);

  const moveStage = async (stage: string) => {
    if (!item) return;
    await fetch(`${API_URL}/landlord/pipeline/${item.id}/stage`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ stage }),
    });
    load();
  };

  const submitComplete = async () => {
    if (!item) return;
    setCompleting(true);
    try {
      const r = await fetch(`${API_URL}/landlord/pipeline/${item.id}/complete`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...completeForm,
          final_price: completeForm.final_price ? Number(completeForm.final_price) : null,
          stamp_duty: completeForm.stamp_duty ? Number(completeForm.stamp_duty) : 0,
          legal_fees: completeForm.legal_fees ? Number(completeForm.legal_fees) : 0,
          agent_fees: completeForm.agent_fees ? Number(completeForm.agent_fees) : 0,
          other_costs: completeForm.other_costs ? Number(completeForm.other_costs) : 0,
          monthly_rent: completeForm.monthly_rent ? Number(completeForm.monthly_rent) : 0,
        }),
      });
      const d = await r.json();
      if (!r.ok) { showToast(d.error || 'Failed'); setCompleting(false); return; }
      showToast('Deal completed — accounting updated');
      setShowComplete(false);
      load();
    } catch { showToast('Network error'); }
    setCompleting(false);
  };

  if (loading || !item) return <div style={{ padding: 80, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>;

  const st = stageMeta(item.stage);
  const isBuy = item.pipeline_type === 'buy';
  const canComplete = item.stage !== 'completed' && item.stage !== 'withdrawn' && !item.accounting_recorded;

  return (
    <div style={{ minHeight: '100vh', background: '#eef2f6', fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '16px 24px' }}>
        <button type="button" onClick={() => navigate('/landlord/pipeline')} style={{ background: 'none', border: 'none', color: '#374151', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginBottom: 8, display: 'block' }}>← Back</button>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800 }}>{item.address}</h1>
            <p style={{ margin: '6px 0 0', color: '#64748b' }}>{item.postcode || '—'} · {isBuy ? 'Buying' : 'Selling'}</p>
          </div>
          <span style={{ background: st.bg, color: st.colour, padding: '6px 14px', borderRadius: 8, fontWeight: 700 }}>{st.label}</span>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
          <button type="button" onClick={() => navigate(`/landlord/pipeline/${id}/edit`)} style={{ padding: '8px 16px', border: `1.5px solid ${UK.blue}`, borderRadius: 8, background: '#fff', color: UK.blue, fontWeight: 700, cursor: 'pointer' }}>Edit</button>
          {canComplete && (
            <button type="button" onClick={() => setShowComplete(true)} style={{ padding: '8px 16px', border: 'none', borderRadius: 8, background: '#16a34a', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>Complete Deal</button>
          )}
          {PIPELINE_STAGES.filter(s => s.id !== item.stage && s.id !== 'withdrawn').map(s => (
            <button key={s.id} type="button" onClick={() => moveStage(s.id)} style={{ padding: '8px 12px', border: `1px solid ${s.colour}44`, borderRadius: 8, background: s.bg, color: s.colour, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>→ {s.label}</button>
          ))}
        </div>
      </div>

      <div style={{ maxWidth: 920, margin: '24px auto', padding: '0 24px' }}>
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: 22, marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 16, fontSize: 14 }}>
            <div><div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 700 }}>ASKING</div><div style={{ fontWeight: 800 }}>{fmtMoney(item.asking_price)}</div></div>
            <div><div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 700 }}>OFFER</div><div style={{ fontWeight: 800, color: UK.blue }}>{fmtMoney(item.offer_price)}</div></div>
            <div><div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 700 }}>TARGET</div><div style={{ fontWeight: 700 }}>{fmt(item.target_date)}</div></div>
            {item.agent_name && <div><div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 700 }}>AGENT</div><div style={{ fontWeight: 700 }}>{item.agent_name}{item.agent_phone ? ` · ${item.agent_phone}` : ''}</div></div>}
            {linked && (
              <div>
                <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 700 }}>LINKED PROPERTY</div>
                <button type="button" onClick={() => navigate(`/landlord/properties/${linked.id}`)} style={{ background: 'none', border: 'none', padding: 0, fontWeight: 700, color: UK.blue, cursor: 'pointer', textDecoration: 'underline', fontSize: 14 }}>
                  {linked.address} →
                </button>
              </div>
            )}
            {item.completion_date && <div><div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 700 }}>COMPLETED</div><div style={{ fontWeight: 700 }}>{fmt(item.completion_date)}</div></div>}
            {item.final_price && <div><div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 700 }}>FINAL PRICE</div><div style={{ fontWeight: 800 }}>{fmtMoney(item.final_price)}</div></div>}
          </div>
          {item.notes && <p style={{ marginTop: 16, fontSize: 13, color: '#64748b', borderTop: '1px solid #f1f5f9', paddingTop: 12 }}>{item.notes}</p>}
        </div>

        {item.accounting_recorded && (
          <div style={{ background: '#eff6ff', border: `1px solid ${UK.blue}33`, borderRadius: 12, padding: 18, marginBottom: 16 }}>
            <div style={{ fontWeight: 800, color: UK.blue, marginBottom: 8 }}>Accounting recorded</div>
            <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>
              {isBuy
                ? 'Acquisition costs posted to Accounting (Stamp Duty / Acquisition Costs). Purchase price is tracked for CGT — not counted as rental expense.'
                : 'Sale proceeds posted as capital income. Disposal costs posted as expenses. Not mixed with rental profit.'}
            </p>
            {transactions.length > 0 && (
              <div style={{ marginTop: 12 }}>
                {transactions.map((t: any) => (
                  <div key={t.id} style={{ fontSize: 12, padding: '6px 0', borderBottom: '1px solid #e2e8f0' }}>
                    {t.type === 'income' ? '+' : '−'}{fmtMoney(t.amount)} · {t.category} · {fmt(t.date)}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {showComplete && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 20px', overflowY: 'auto' }} onClick={() => setShowComplete(false)}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 28, maxWidth: 520, width: '100%' }} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 800 }}>Complete {isBuy ? 'Purchase' : 'Sale'}</h2>
            <p style={{ margin: '0 0 16px', fontSize: 12, color: '#64748b' }}>
              {isBuy
                ? 'Costs (stamp duty, legal) go to Accounting. Purchase price is not a rental expense.'
                : 'Sale price records as capital income. Legal/agent fees as disposal costs.'}
            </p>
            <div style={{ display: 'grid', gap: 12 }}>
              <div><label style={{ fontSize: 12, fontWeight: 600 }}>Completion date</label><input type="date" value={completeForm.completion_date} onChange={e => setCompleteForm(f => ({ ...f, completion_date: e.target.value }))} style={{ width: '100%', padding: 8, borderRadius: 8, border: '1px solid #d1d5db', marginTop: 4 }} /></div>
              <div><label style={{ fontSize: 12, fontWeight: 600 }}>{isBuy ? 'Purchase price (£)' : 'Sale price (£)'}</label><input type="number" value={completeForm.final_price} onChange={e => setCompleteForm(f => ({ ...f, final_price: e.target.value }))} style={{ width: '100%', padding: 8, borderRadius: 8, border: '1px solid #d1d5db', marginTop: 4 }} /></div>
              {isBuy && <div><label style={{ fontSize: 12, fontWeight: 600 }}>Stamp duty (£)</label><input type="number" value={completeForm.stamp_duty} onChange={e => setCompleteForm(f => ({ ...f, stamp_duty: e.target.value }))} style={{ width: '100%', padding: 8, borderRadius: 8, border: '1px solid #d1d5db', marginTop: 4 }} /></div>}
              <div><label style={{ fontSize: 12, fontWeight: 600 }}>Legal / solicitor (£)</label><input type="number" value={completeForm.legal_fees} onChange={e => setCompleteForm(f => ({ ...f, legal_fees: e.target.value }))} style={{ width: '100%', padding: 8, borderRadius: 8, border: '1px solid #d1d5db', marginTop: 4 }} /></div>
              <div><label style={{ fontSize: 12, fontWeight: 600 }}>Agent fees (£)</label><input type="number" value={completeForm.agent_fees} onChange={e => setCompleteForm(f => ({ ...f, agent_fees: e.target.value }))} style={{ width: '100%', padding: 8, borderRadius: 8, border: '1px solid #d1d5db', marginTop: 4 }} /></div>
              <div><label style={{ fontSize: 12, fontWeight: 600 }}>Other costs (£)</label><input type="number" value={completeForm.other_costs} onChange={e => setCompleteForm(f => ({ ...f, other_costs: e.target.value }))} style={{ width: '100%', padding: 8, borderRadius: 8, border: '1px solid #d1d5db', marginTop: 4 }} /></div>
              {isBuy && (
                <>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600 }}>
                    <input type="checkbox" checked={completeForm.add_to_portfolio} onChange={e => setCompleteForm(f => ({ ...f, add_to_portfolio: e.target.checked }))} />
                    Add to portfolio as new property
                  </label>
                  {completeForm.add_to_portfolio && (
                    <div><label style={{ fontSize: 12, fontWeight: 600 }}>Expected monthly rent (£)</label><input type="number" value={completeForm.monthly_rent} onChange={e => setCompleteForm(f => ({ ...f, monthly_rent: e.target.value }))} style={{ width: '100%', padding: 8, borderRadius: 8, border: '1px solid #d1d5db', marginTop: 4 }} /></div>
                  )}
                </>
              )}
              {!isBuy && item.linked_property_id && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600 }}>
                  <input type="checkbox" checked={completeForm.archive_property} onChange={e => setCompleteForm(f => ({ ...f, archive_property: e.target.checked }))} />
                  Archive linked property from portfolio
                </label>
              )}
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button type="button" onClick={submitComplete} disabled={completing} style={{ flex: 1, padding: 10, background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>{completing ? 'Saving…' : 'Complete & Record'}</button>
              <button type="button" onClick={() => setShowComplete(false)} style={{ padding: '10px 16px', background: '#f1f5f9', border: '1px solid #d1d5db', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
